<template>
  <div class="relative h-full">
    <div class="z-50 absolute w-full h-full bg-gray-200 bg-opacity-90 flex justify-center items-center" v-if="loadingMessage">
      <div class="text-xl font-bold">{{ loadingMessage }}</div>
    </div>
    <div class="py-4 select-none w-full">
      <div class="flex flex-col items-center justify-center min-h-screen">
        <template v-if="hidden">
          <h1 class="text-3xl font-bold text-info-600 mb-4">Newsdesk Inventory Asset #{{ uuid }}</h1>
        </template>
        <template v-else-if="state === 'initial' || state === 'success'">
          <h1 class="text-3xl font-bold text-blue-800 mb-4">Welcome to Newsdesk!</h1>
          <p class="text-lg text-center text-gray-700 mb-6">You have reached this page by scanning the Inventory Asset QR code.</p>
          <template v-if="inventoryAsset">
            <p class="text-lg text-center text-gray-700 mb-6">
              Here’s the information about the scanned asset:
              <br />
              <strong
                >Label: <em> {{ inventoryAsset.label }} </em>
              </strong>
              <br />
              <strong
                >Description: <em> {{ inventoryAsset.description }} </em>
              </strong>
            </p>
            <template v-if="hasUserData">
              <p class="text-lg text-center text-gray-700 mb-6">
                If you’d like to update or manage this Inventory Asset Checkouts,
                <br />
                please proceed to the Inventory Asset Page.
              </p>
              <button
                class="bg-blue-800 text-white px-4 py-2 rounded shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50"
                @click="redirectToInventoryAssetEditPage"
              >
                Open Inventory Asset Page
              </button>
              <p class="text-lg text-center text-gray-700 my-6">OR</p>
              <p class="text-lg text-center text-gray-700 mb-6">
                For a more convenient use on mobile devices,
                <br />
                try using the Inventory Asset Page in the Mobile App.
              </p>
              <button
                class="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50"
                @click="redirectToInventoryAssetMobileApp"
              >
                Open Mobile App
              </button>
              <template v-if="showAppDownloadLinks && (iosLink || androidLink)">
                <div
                  class="mt-16 font-semibold cursor-pointer text-lg text-center text-gray-700 mb-6"
                  @click="showMobileAppDownloadLinks = !showMobileAppDownloadLinks"
                >
                  Mobile App Downloads
                </div>
                <div v-if="showMobileAppDownloadLinks" class="text-gray-800 w-full flex flex-col gap-4">
                  <div v-if="iosLink" class="flex flex-col items-center">
                    <div class="text-lg font-bold text-blue-800">
                      <a :href="iosLink" target="_blank" rel="noopener noreferrer">IOS</a>
                    </div>
                    <div class="text-base text-center">
                      To add E.W. Scripps as a trusted developer, go to <span class="font-semibold">Settings > General > VPN & Device Management</span>.
                      <div><a href="https://support.apple.com/en-us/118254" class="font-medium text-blue-600">Learn more</a></div>
                    </div>
                  </div>
                  <div v-if="androidLink" class="flex flex-col items-center gap-4">
                    <div class="text-lg font-bold text-blue-800">
                      <a :href="androidLink" target="_blank" rel="noopener noreferrer">Android</a>
                    </div>
                  </div>
                </div>
              </template>
            </template>
            <template v-else>
              <p class="text-lg text-center text-gray-700 mb-6">To update or manage this Inventory Asset Checkouts data, you must be logged in.</p>
              <button
                class="bg-blue-800 text-white px-4 py-2 rounded shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50"
                @click="redirectToInventoryAssetEditPage"
              >
                Login
              </button>
              <p class="text-lg text-center text-gray-700 my-6">OR</p>
              <p class="text-lg text-center text-gray-700 mb-6">
                For a more convenient use on mobile devices,
                <br />
                you can also log in via the Mobile App to manage this Inventory Asset.
              </p>
              <button
                class="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50"
                @click="redirectToInventoryAssetMobileApp"
              >
                Login via Mobile App
              </button>
              <template v-if="showAppDownloadLinks && (iosLink || androidLink)">
                <div
                  class="mt-16 font-semibold cursor-pointer text-lg text-center text-gray-700 mb-6"
                  @click="showMobileAppDownloadLinks = !showMobileAppDownloadLinks"
                >
                  Mobile App Downloads
                </div>
                <div v-if="showMobileAppDownloadLinks" class="text-gray-800 w-full flex flex-col gap-4">
                  <div v-if="iosLink" class="flex flex-col items-center">
                    <div class="text-lg font-bold text-blue-800">
                      <a :href="iosLink" target="_blank" rel="noopener noreferrer">IOS</a>
                    </div>
                    <div class="text-base text-center">
                      To add E.W. Scripps as a trusted developer, go to <span class="font-semibold">Settings > General > VPN & Device Management</span>.
                      <div><a href="https://support.apple.com/en-us/118254" class="font-medium text-blue-600">Learn more</a></div>
                    </div>
                  </div>
                  <div v-if="androidLink" class="flex flex-col items-center gap-4">
                    <div class="text-lg font-bold text-blue-800">
                      <a :href="androidLink" target="_blank" rel="noopener noreferrer">Android</a>
                    </div>
                  </div>
                </div>
              </template>
            </template>
          </template>
        </template>
        <template v-else-if="state === 'check'">
          <h1 class="text-3xl font-bold text-gray-800 mb-4">Welcome to Newsdesk!</h1>
          <p class="text-lg text-center text-gray-700 mb-6">You have reached this page by scanning the Inventory Asset QR code.</p>
          <h1 class="text-3xl font-bold text-blue-800 mb-4">Checking Inventory Asset...</h1>
          <p class="text-lg text-center text-gray-700 mb-6">
            Please wait while we verify your link.
            <br />
            This may take a few moments.
          </p>
        </template>
        <template v-else-if="state === 'error'">
          <h1 class="text-3xl font-bold text-red-800 mb-4">Welcome to Newsdesk!</h1>
          <p class="text-lg text-center text-gray-700 mb-6">You have reached this page by scanning the Inventory Asset QR code.</p>
          <h1 class="text-3xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p class="text-lg text-center text-gray-700 mb-6">The link you have used is invalid.<br />Please contact your station manager.</p>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/config/api'
import localstore from 'store'

export default {
  components: {},
  data() {
    return {
      hidden: false,
      state: 'initial', // initial/check/success/error
      loadingMessage: false,
      tokenFieldValue: '',
      checkToken: false,
      hasUserData: false,
      redirectPath: '',
      inventoryAsset: null,
      showMobileAppDownloadLinks: false,
      showAppDownloadLinks: false
    }
  },
  computed: {
    uuid() {
      return this.$route.params.uuid || ''
    },
    iosLink() {
      return import.meta.env.VITE_IOS_LINK
    },
    androidLink() {
      return import.meta.env.VITE_ANDROID_LINK
    }
  },
  watch: {},
  async mounted() {
    const userData = localstore.get('ews_user', {})
    if (userData?.profile?.empid) {
      this.hasUserData = true
    }
    if (this.uuid) {
      await this.getInventoryAsset()
    }
  },
  beforeUnmount() {},
  methods: {
    async getInventoryAsset() {
      const uuidValue = this.uuid || ''

      this.state = 'check'

      api
        .get(`/v2/newsdesk/inventory-assets/by-uuid/${uuidValue}`)
        .then((response) => {
          if (response.data.id) {
            this.inventoryAsset = response.data
            this.state = 'success'
          } else {
            this.state = 'error'
          }
        })
        .catch((error) => {
          this.state = 'error'
        })
    },
    redirectToInventoryAssetEditPage() {
      if (this.inventoryAsset) {
        this.$router.push({ path: `/equipment/${this.inventoryAsset.uuid}?station=${this.inventoryAsset.station}` }).catch(() => {})
      }
      return
    },
    redirectToInventoryAssetMobileApp() {
      if (this.inventoryAsset) {
        window.location = `newsdesk://navigate/equipment?uuid=${this.uuid}&station=${this.inventoryAsset.station}`
      }
      window.location = `newsdesk://navigate/equipment?uuid=${this.uuid}`
      return
    }
  }
}
</script>

<style lang="postcss" scoped></style>
